import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Minus, Plus } from "@untitledui/icons";
import { type ReactElement, type SyntheticEvent, useState } from "react";
import type { Key } from "react-aria-components";
import { Button as AriaButton } from "react-aria-components";
import { DisplayFieldShell } from "@/components/Atoms/DisplayField/DisplayFieldShell";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { assetService } from "@/services/asset/asset.service";
import { jobService } from "@/services/job/job.service";
import { recordService } from "@/services/record/record.service";
import type { AttachmentRequirementDisplay } from "@/services/shared/attachment-requirement.types";
import { ticketService } from "@/services/ticket/ticket.service";
import { showApiErrorToast } from "@/utils/toast.utils";
import { ConfigureRequiredAttachmentsModal } from "./configure-required-attachments-modal";
import { useCanEditTypeAttachmentRequirement } from "./type-attachment-requirement-action";
import {
	type AttachmentTagDraft,
	type AttachmentTagSnapshot,
	diffAttachmentTags,
} from "./diff-attachment-tags";
import {
	attachmentPolicyMode,
	AttachmentPolicyMode,
	requiredAttachmentsFieldPresentation,
	RequiredAttachmentsFieldPresentation,
} from "./partition-attachments";

export type AttachmentRequirementEntityKind =
	| "job"
	| "ticket"
	| "asset"
	| "record";

const MODE_NONE = "none";
const MODE_MINIMUM = "minimum";
const MODE_NAMED = "named";

/** Keeps controls inside a menu row from selecting that row. */
function stopMenuEvent(event: SyntheticEvent) {
	event.stopPropagation();
}

const menuControlHandlers = {
	onPointerDown: stopMenuEvent,
	onPointerUp: stopMenuEvent,
	onMouseDown: stopMenuEvent,
	onMouseUp: stopMenuEvent,
	onClick: stopMenuEvent,
	onKeyDown: stopMenuEvent,
	onKeyUp: stopMenuEvent,
};

function kindLabel(
	t: (key: string, options?: Record<string, unknown>) => string,
	allowedKind: string,
): string {
	if (allowedKind === "photo")
		return t("admin.attachmentRequirements.kind.photo");
	if (allowedKind === "file")
		return t("admin.attachmentRequirements.kind.file");
	return t("admin.attachmentRequirements.kind.any");
}

async function putMinimum(
	kind: AttachmentRequirementEntityKind,
	typeId: string,
	minimum: number,
	organizationId?: string,
) {
	const options = { organizationId };
	if (kind === "job") {
		await jobService.putJobTypeAttachmentRequirement(typeId, minimum, options);
		return;
	}
	if (kind === "ticket") {
		await ticketService.putTicketTypeAttachmentRequirement(
			typeId,
			minimum,
			options,
		);
		return;
	}
	if (kind === "asset") {
		await assetService.putAssetTypeAttachmentRequirement(
			typeId,
			minimum,
			options,
		);
		return;
	}
	await recordService.putRecordTypeAttachmentRequirement(
		typeId,
		minimum,
		options,
	);
}

async function deleteTag(
	kind: AttachmentRequirementEntityKind,
	typeId: string,
	tagId: string,
	organizationId?: string,
) {
	const options = { organizationId };
	if (kind === "job") {
		await jobService.deleteJobTypeAttachmentRequirementTag(
			typeId,
			tagId,
			options,
		);
		return;
	}
	if (kind === "ticket") {
		await ticketService.deleteTicketTypeAttachmentRequirementTag(
			typeId,
			tagId,
			options,
		);
		return;
	}
	if (kind === "asset") {
		await assetService.deleteAssetTypeAttachmentRequirementTag(
			typeId,
			tagId,
			options,
		);
		return;
	}
	await recordService.deleteRecordTypeAttachmentRequirementTag(
		typeId,
		tagId,
		options,
	);
}

async function createTag(
	kind: AttachmentRequirementEntityKind,
	typeId: string,
	draft: AttachmentTagDraft,
	sortOrder: number,
	organizationId?: string,
) {
	const info = {
		name: draft.name.trim(),
		description: draft.description.trim() || null,
		allowedKind: draft.allowedKind,
		minimumCount: draft.minimumCount,
		sortOrder,
	};
	const options = { organizationId };
	if (kind === "job") {
		await jobService.createJobTypeAttachmentRequirementTag(
			typeId,
			info,
			options,
		);
		return;
	}
	if (kind === "ticket") {
		await ticketService.createTicketTypeAttachmentRequirementTag(
			typeId,
			info,
			options,
		);
		return;
	}
	if (kind === "asset") {
		await assetService.createAssetTypeAttachmentRequirementTag(
			typeId,
			info,
			options,
		);
		return;
	}
	await recordService.createRecordTypeAttachmentRequirementTag(
		typeId,
		info,
		options,
	);
}

async function editTag(
	kind: AttachmentRequirementEntityKind,
	typeId: string,
	tagId: string,
	draft: AttachmentTagDraft,
	sortOrder: number,
	organizationId?: string,
) {
	const info = {
		name: draft.name.trim(),
		description: draft.description.trim() || null,
		allowedKind: draft.allowedKind,
		minimumCount: draft.minimumCount,
		sortOrder,
	};
	const options = { organizationId };
	if (kind === "job") {
		await jobService.editJobTypeAttachmentRequirementTag(
			typeId,
			tagId,
			info,
			options,
		);
		return;
	}
	if (kind === "ticket") {
		await ticketService.editTicketTypeAttachmentRequirementTag(
			typeId,
			tagId,
			info,
			options,
		);
		return;
	}
	if (kind === "asset") {
		await assetService.editAssetTypeAttachmentRequirementTag(
			typeId,
			tagId,
			info,
			options,
		);
		return;
	}
	await recordService.editRecordTypeAttachmentRequirementTag(
		typeId,
		tagId,
		info,
		options,
	);
}

function invalidateTypeDetail(
	queryClient: ReturnType<typeof useQueryClient>,
	kind: AttachmentRequirementEntityKind,
	typeId: string,
) {
	const marker =
		kind === "job"
			? "job-type-detail"
			: kind === "ticket"
				? "ticket-type-detail"
				: "detail";
	void queryClient.invalidateQueries({
		predicate: (query) =>
			query.queryKey.includes(marker) && query.queryKey.includes(typeId),
	});
}

export function RequiredAttachmentsField({
	entityKind,
	typeId,
	organizationId: organizationIdProp,
	requirement,
}: {
	entityKind: AttachmentRequirementEntityKind;
	typeId: string;
	organizationId?: string;
	requirement?: AttachmentRequirementDisplay | null;
}): ReactElement {
	const { t } = useAppTranslation();
	const { allowed: canEdit } = useCanEditTypeAttachmentRequirement(
		entityKind,
		typeId,
	);
	const params = useParams({ strict: false }) as { orgId?: string };
	const organizationId = organizationIdProp ?? params.orgId;
	const queryClient = useQueryClient();
	const [feedback, setFeedback] = useState<DisplayFieldFeedback>(
		DisplayFieldFeedback.Idle,
	);
	const [menuOpen, setMenuOpen] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [countDraft, setCountDraft] = useState<number | null>(null);
	const [tagsExpanded, setTagsExpanded] = useState(false);

	const mode = attachmentPolicyMode(requirement);
	const presentation = requiredAttachmentsFieldPresentation(requirement);
	const minimum =
		countDraft ??
		(requirement && requirement.minimumTotalAttachments > 0
			? requirement.minimumTotalAttachments
			: 1);
	const tags = requirement?.tags ?? [];
	const showTagSection = mode === AttachmentPolicyMode.Named && tags.length > 0;

	const hasSavedRequirement =
		presentation !== RequiredAttachmentsFieldPresentation.Unset;
	const fieldLabel = hasSavedRequirement
		? t("admin.attachmentRequirements.field.selectedLabel")
		: t("admin.attachmentRequirements.field.label");
	const valueLabel =
		presentation === RequiredAttachmentsFieldPresentation.Minimum
			? t("admin.attachmentRequirements.mode.minimumValue", {
					count: requirement?.minimumTotalAttachments ?? minimum,
				})
			: presentation === RequiredAttachmentsFieldPresentation.Named
				? t("admin.attachmentRequirements.mode.namedCount", {
						count: tags.length,
					})
				: t("admin.attachmentRequirements.mode.none");

	const changeMinimum = (next: number) => {
		const clamped = Math.max(1, next);
		setCountDraft(clamped);
		if (mode === AttachmentPolicyMode.Minimum) {
			void saveMinimum(clamped);
		}
	};

	const finishSaved = async () => {
		invalidateTypeDetail(queryClient, entityKind, typeId);
		setFeedback(DisplayFieldFeedback.Saved);
		window.setTimeout(() => {
			setFeedback(DisplayFieldFeedback.Idle);
		}, 1600);
	};

	const runSave = async (work: () => Promise<void>) => {
		setIsSaving(true);
		setFeedback(DisplayFieldFeedback.Saving);
		try {
			await work();
			setCountDraft(null);
			await finishSaved();
		} catch (error) {
			setFeedback(DisplayFieldFeedback.Idle);
			showApiErrorToast({
				error,
				message: t("admin.attachmentRequirements.errors.saveFailed"),
			});
		} finally {
			setIsSaving(false);
		}
	};

	const saveNone = () =>
		runSave(async () => {
			for (const tag of tags) {
				await deleteTag(entityKind, typeId, tag.id, organizationId);
			}
			await putMinimum(entityKind, typeId, 0, organizationId);
		});

	const saveMinimum = (nextCount: number) =>
		runSave(async () => {
			await putMinimum(entityKind, typeId, nextCount, organizationId);
		});

	const saveNamed = (drafts: AttachmentTagDraft[]) => {
		if (drafts.length === 0) return;
		return runSave(async () => {
			if (requirement && requirement.minimumTotalAttachments > 0) {
				await putMinimum(entityKind, typeId, 0, organizationId);
			}
			const snapshots: AttachmentTagSnapshot[] = tags.map((tag) => ({
				id: tag.id,
				name: tag.name,
				description: tag.description,
				allowedKind: tag.allowedKind,
				minimumCount: tag.minimumCount,
				sortOrder: tag.sortOrder,
			}));
			const ops = diffAttachmentTags(snapshots, drafts);
			for (const op of ops) {
				if (op.type === "delete") {
					await deleteTag(entityKind, typeId, op.id, organizationId);
				}
			}
			for (const op of ops) {
				if (op.type === "update") {
					await editTag(
						entityKind,
						typeId,
						op.id,
						op.draft,
						op.sortOrder,
						organizationId,
					);
				}
			}
			for (const op of ops) {
				if (op.type === "create") {
					await createTag(
						entityKind,
						typeId,
						op.draft,
						op.sortOrder,
						organizationId,
					);
				}
			}
			setModalOpen(false);
		});
	};

	const handleAction = (key: Key) => {
		const id = String(key);
		if (id === MODE_NONE) {
			setMenuOpen(false);
			if (presentation !== RequiredAttachmentsFieldPresentation.None) {
				void saveNone();
			}
			return;
		}
		if (id === MODE_MINIMUM) {
			setMenuOpen(false);
			if (mode !== AttachmentPolicyMode.Minimum) void saveMinimum(minimum);
			return;
		}
		if (id === MODE_NAMED) {
			setMenuOpen(false);
			setModalOpen(true);
		}
	};

	const tagSection = showTagSection ? (
		<NamedTagSection
			tags={tags}
			expanded={tagsExpanded}
			onToggle={() => setTagsExpanded((current) => !current)}
			kindLabel={(kind) => kindLabel(t, kind)}
		/>
	) : null;

	if (!canEdit) {
		return (
			<div className="col-span-2">
				<div className="flex w-full flex-col border-b-2 border-border-secondary">
					<DisplayFieldShell
						label={fieldLabel}
						state={DisplayFieldState.Enabled}
						isPopulated={hasSavedRequirement}
						showEdit={false}
						showUnderline={false}
						data-testid="required-attachments-field"
					>
						<span className="w-full truncate font-display text-sm font-extrabold text-primary">
							{valueLabel}
						</span>
					</DisplayFieldShell>
					{tagSection}
				</div>
			</div>
		);
	}

	return (
		<div className="col-span-2">
			<div className="flex w-full flex-col border-b-2 border-border-secondary">
				<Dropdown.Root isOpen={menuOpen} onOpenChange={setMenuOpen}>
					<AriaButton className="w-full border-0 bg-transparent p-0 text-left outline-focus-ring">
						<DisplayFieldShell
							label={fieldLabel}
							state={DisplayFieldState.Enabled}
							isPopulated={hasSavedRequirement}
							feedback={feedback}
							showEdit={feedback === DisplayFieldFeedback.Idle}
							isTriggerChild
							showUnderline={false}
							data-testid="required-attachments-field"
						>
							<span className="w-full truncate font-display text-sm font-extrabold text-primary">
								{valueLabel}
							</span>
						</DisplayFieldShell>
					</AriaButton>
					<Dropdown.Popover className="w-(--trigger-width)">
						<Dropdown.Menu
							aria-label={t("admin.attachmentRequirements.field.label")}
							selectionMode="single"
							selectedKeys={
								presentation === RequiredAttachmentsFieldPresentation.Minimum
									? [MODE_MINIMUM]
									: presentation === RequiredAttachmentsFieldPresentation.Named
										? [MODE_NAMED]
										: presentation === RequiredAttachmentsFieldPresentation.None
											? [MODE_NONE]
											: []
							}
							onAction={handleAction}
						>
							<Dropdown.Item
								id={MODE_NONE}
								label={t("admin.attachmentRequirements.mode.none")}
							/>
							<Dropdown.Item
								id={MODE_MINIMUM}
								textValue={t("admin.attachmentRequirements.mode.minimum")}
							>
								<span className="flex w-full items-center justify-between gap-3">
									<span>{t("admin.attachmentRequirements.mode.minimum")}</span>
									<span
										className="inline-flex shrink-0 items-center overflow-hidden rounded-lg bg-primary shadow-xs ring-1 ring-primary ring-inset"
										data-testid="required-attachments-minimum-stepper"
										{...menuControlHandlers}
									>
										<AriaButton
											aria-label={t(
												"admin.attachmentRequirements.minimum.decrease",
											)}
											isDisabled={minimum <= 1 || isSaving}
											onPress={() => changeMinimum(minimum - 1)}
											className="flex size-9 items-center justify-center border-r border-primary text-fg-secondary outline-focus-ring hover:bg-primary_hover focus-visible:outline-2 focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:text-fg-disabled"
										>
											<Minus className="size-5" aria-hidden />
										</AriaButton>
										<span
											className="flex h-9 min-w-10 items-center justify-center px-2 text-sm font-medium text-primary"
											aria-live="polite"
										>
											{minimum}
										</span>
										<AriaButton
											aria-label={t(
												"admin.attachmentRequirements.minimum.increase",
											)}
											isDisabled={isSaving}
											onPress={() => changeMinimum(minimum + 1)}
											className="flex size-9 items-center justify-center border-l border-primary text-fg-secondary outline-focus-ring hover:bg-primary_hover focus-visible:outline-2 focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:text-fg-disabled"
										>
											<Plus className="size-5" aria-hidden />
										</AriaButton>
									</span>
								</span>
							</Dropdown.Item>
							<Dropdown.Item
								id={MODE_NAMED}
								textValue={t("admin.attachmentRequirements.mode.named")}
							>
								<span className="flex w-full items-center justify-between gap-3">
									<span>{t("admin.attachmentRequirements.mode.named")}</span>
									<span className="shrink-0" {...menuControlHandlers}>
										<Button
											color="secondary"
											size="sm"
											data-testid="required-attachments-configure"
											onPress={() => {
												setMenuOpen(false);
												setModalOpen(true);
											}}
										>
											{t("admin.attachmentRequirements.configure")}
										</Button>
									</span>
								</span>
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown.Popover>
				</Dropdown.Root>
				{tagSection}
			</div>
			<ConfigureRequiredAttachmentsModal
				isOpen={modalOpen}
				tags={tags}
				isSaving={isSaving}
				onOpenChange={(open) => {
					if (!isSaving) setModalOpen(open);
				}}
				onSave={(drafts) => {
					void saveNamed(drafts);
				}}
			/>
		</div>
	);
}

function NamedTagSection({
	tags,
	expanded,
	onToggle,
	kindLabel: labelForKind,
}: {
	tags: AttachmentRequirementDisplay["tags"];
	expanded: boolean;
	onToggle: () => void;
	kindLabel: (allowedKind: string) => string;
}): ReactElement {
	const { t } = useAppTranslation();
	const ordered = [...tags].sort((a, b) => a.sortOrder - b.sortOrder);
	return (
		<div
			className="flex flex-col gap-2 px-4 pb-2"
			data-testid="required-attachments-tags"
		>
			<button
				type="button"
				className="self-start text-sm font-semibold text-brand-secondary outline-focus-ring hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
				aria-expanded={expanded}
				data-testid="required-attachments-tags-toggle"
				onClick={onToggle}
			>
				{expanded
					? t("admin.attachmentRequirements.tags.hide")
					: t("admin.attachmentRequirements.tags.show")}
			</button>
			{expanded ? (
				<ul className="flex flex-col gap-1.5">
					{ordered.map((tag) => (
						<li key={tag.id} className="flex flex-col text-sm">
							<span className="font-medium text-primary">{tag.name}</span>
							<span className="text-secondary">
								{t("admin.attachmentRequirements.tag.meta", {
									kind: labelForKind(tag.allowedKind),
									count: tag.minimumCount,
								})}
							</span>
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}
